import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

/**
 * Whether a period has ended, and so could have been declared at all.
 *
 * Mirrors `VATReportingService._validatePeriodCanBeHandled`, which refuses while
 * `today <= end_date` with VAT_PERIOD_NOT_ELAPSED. That guard is the authority
 * and stays; this only stops the UI offering an action that cannot succeed —
 * both close surfaces used to show the button for a period still running, so
 * clicking it produced an error instead of nothing happening.
 *
 * Accepts either a 'YYYY-MM-DD' date or an ISO timestamp.
 */
export function hasPeriodEnded(period, now = new Date()) {
    const raw = period?.end_date;
    if (!raw) return false;
    const end = new Date(`${String(raw).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(end.getTime())) return false;
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    return today > end;
}

/**
 * Mark a VAT period as already filed in another system.
 *
 * Shared because the two surfaces that offer this had drifted apart: the
 * periods list prompted for the figure that was actually declared and surfaced
 * the server's message, while the wizard's header button posted only the
 * fiscal year — no amount, no note — and swallowed every error into "Kunde inte
 * stänga momsperioden".
 *
 * That difference matters more since the eligibility proxy was removed: the
 * recorded assertion IS the control now, so a surface that offers the action
 * without recording it properly is the whole control missing. G43 put it in the
 * service's own words — a historical close that records a vat_total of 0 is not
 * "we don't know", it is "no VAT".
 *
 * @returns {Promise<{closed: boolean, error?: string}>} `closed:false` with no
 *   error means the user cancelled.
 */
export async function promptAndCloseHistoricalPeriod({ companyId, period, fiscalYearId }) {
    const swal = (await import('sweetalert')).default;

    const filedTotal = await swal({
        title: 'Redan rapporterad period',
        text: `Perioden ${period.start_date} – ${period.end_date} markeras som redan hanterad — `
            + 'använd det för perioder som rapporterats i ett annat system.\n\n'
            + 'Ange den moms som faktiskt deklarerades (fält 49), om du har den. '
            + 'Lämnar du fältet tomt stängs perioden ändå, men utan siffra.',
        icon: 'warning',
        content: {
            element: 'input',
            attributes: { placeholder: 'Moms att betala/få tillbaka, kr', type: 'number', step: '1' },
        },
        buttons: {
            cancel: { text: 'Avbryt', value: null, visible: true },
            confirm: { text: 'Stäng period', value: '', visible: true },
        },
    });

    if (filedTotal === null) return { closed: false };

    const blank = String(filedTotal).trim() === '';
    try {
        await axios.post(
            getApiUrl(`${companyId}/reporting/vat-period/${period.vat_reporting_period_id}/close-historical`),
            {
                fiscalYearId: fiscalYearId || period.fiscal_year_id,
                externally_filed_vat_total: blank ? null : Number(filedTotal),
                externally_filed_note: blank
                    ? 'Lämnad i ett annat system; belopp inte registrerat.'
                    : 'Lämnad i ett annat system.',
            },
            { withCredentials: true }
        );
        return { closed: true };
    } catch (err) {
        // The server's own message, not a generic one: it is the only place the
        // reason for a refusal exists.
        return { closed: false, error: err.response?.data?.message || 'Kunde inte stänga perioden' };
    }
}

export default promptAndCloseHistoricalPeriod;
